<template>
  <div>
    <app-template />
    <div class="uk-container uk-container-large">
      <h1>Chats</h1>
      <div class="tabs-wrapper">
        <section class="tab-content">
          <div class="multi-chats-wrapper">
            <div
              class="chat-group-wrapper club-groups"
              v-show="showClubGroups"
            >
              <h3>Club groups</h3>
              <p class="club-groups-hint" v-if="userData.userType === 'Admin'">
                Not tied to a team — e.g. academy coaches, physio, sports
                science. Only the people you add can see them.
              </p>
              <chats-component
                v-if="
                  authReady &&
                  userData &&
                  userData.clubId &&
                  teamChatMessages &&
                  clubMembers.length
                "
                :key="'club-' + chatComponentKey"
                :userData="userData"
                :selectedTeam="clubPseudoTeam"
                :currentTeam="clubMembers"
                @getNewDataFromCollection="getNewDataFromCollection"
                @getChatGroupInfo="onClubGroupsLoaded"
                :teamChatMessages="teamChatMessages"
              />
            </div>
            <div
              class="chat-group-wrapper"
              v-for="team in clubTeams"
              :key="team.id"
            >
              <h3>{{ team.teamName }}</h3>
              <chats-component
                v-if="
                  authReady && userData && userData.clubId && teamChatMessages
                "
                :key="chatComponentKey"
                :userData="userData"
                :selectedTeam="team"
                :currentTeam="team.playersInTeam"
                @getNewDataFromCollection="getNewDataFromCollection"
                :teamChatMessages="teamChatMessages"
              />
              <div v-else class="loading-state">
                <p>Loading chat...</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CHECK_PROFILE_HAS_DATA, USER_POPULATE } from "@/store/actions/user.js";
import { Users } from "@/services/user-service.js";
import { Teams } from "@/services/teams-service.js";
import ChatsComponent from "../../components/Chats";
import removeDuplicatesMixin from "@/mixin/removeDuplicates.js";
import checkFBauthStateMixin from "@/mixin/checkFBauthState.js";
import AppTemplate from "../layouts/App";
import {
  where,
  orderBy,
  query,
  limit,
  collection,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import dayjs from "dayjs";

export default {
  name: "Chats",
  mixins: [removeDuplicatesMixin, checkFBauthStateMixin],
  components: { AppTemplate, ChatsComponent },
  data() {
    return {
      publicPath: process.env.BASE_URL,
      editMode: false,
      loadingStuff: false,
      chatComponentKey: 0,
      currentTeam: [],
      clubTeams: [],
      clubMembers: [],
      clubGroupCount: 0,
      teamChats: [],
      teamChatMessages: [],
      selectedUserValue: "",
      weeklyDate: "",
      listOfSelectedplayerIds: [],
      userHeaderData: {
        name: "",
        userType: "",
      },
      userData: {
        name: "",
        userType: "",
      },
      chatListener: null, // Track Firebase listener
    };
  },
  computed: {
    ...mapGetters(["getProfile", "getCoachesUsersData", "getWeekNumber"]),
    // teamId 0 tells the Chats component (and the backend) these are the
    // club's team-less groups.
    clubPseudoTeam() {
      return {
        id: 0,
        teamName: "Club",
        age_group: 0,
        playersInTeam: this.clubMembers,
      };
    },
    // Admins always get the section (it's where they create groups);
    // everyone else only once they're in at least one.
    showClubGroups() {
      return this.userData.userType === "Admin" || this.clubGroupCount > 0;
    },
  },
  async created() {
    // Wait for auth to be ready before proceeding
    await this.waitForAuth();

    this.$store.dispatch(CHECK_PROFILE_HAS_DATA).then(async (response) => {
      if (response === true) {
        this.setUser();
        await this.getUsersTeams();
      } else {
        let clientId = JSON.parse(
          localStorage.getItem("the_w_selectedClientId"),
        );
        const userResult = await this.getUserById(clientId);
        if (userResult === true) {
          this.selectedUserValue = clientId;
          await this.getUsersTeams();
        }
      }

      // Only initialize Firebase operations after auth is ready AND userData is populated
      if (this.authReady && this.userData && this.userData.clubId) {
        await this.initializeFirebaseOperations();
      }
    });
  },
  beforeUnmount() {
    // Clean up Firebase listeners to prevent memory leaks
    if (this.chatListener) {
      this.chatListener();
      this.chatListener = null;
    }
  },
  methods: {
    async initializeFirebaseOperations() {
      try {
        // Use the enhanced auth and Firebase checking method
        await this.executeWithFirebase(async (_auth, firebaseDb) => {
          // Ensure userData and clubId are available
          if (!this.userData || !this.userData.clubId) {
            console.warn(
              "userData or clubId not available for Firebase operations",
            );
            return;
          }

          const clubIdString = this.userData.clubId.toString();

          try {
            this.teamChatMessages = query(
              collection(firebaseDb, "team_chat_messages"),
              where("clubId", "==", clubIdString),
            );

          } catch (queryError) {
            console.error("Error creating Firebase queries:", queryError);
            throw queryError;
          }
        });
      } catch (error) {
        console.error("Error initializing Firebase operations:", error);

        // Retry initialization if it's a Firebase setup issue
        if (
          error.message.includes("_freezeSettings") ||
          error.message.includes("Firestore")
        ) {
          console.log("Retrying Firebase initialization in 3 seconds...");
          setTimeout(() => {
            this.initializeFirebaseOperations();
          }, 3000);
        }
      }
    },

    forceRerender() {
      this.chatComponentKey += 1;
    },

    getNewDataFromCollection() {
      this.forceRerender();
    },

    async getUserByTeamId(id) {
      const userData = {
        teamId: id,
        action: "getUserByTeamId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        return responce.data;
      }
    },
    async populateAllPlayersInTeam() {
      // Null safety check
      if (!Array.isArray(this.clubTeams)) {
        this.clubTeams = [];
        return;
      }

      // Create a copy of clubTeams to avoid direct mutation
      const updatedTeams = [...this.clubTeams];

      for (let i = 0; i < updatedTeams.length; i++) {
        const team = updatedTeams[i];
        if (!team || !team.id) {
          console.warn("Invalid team data at index:", i);
          continue;
        }

        try {
          const playersInTeam = await this.getUserByTeamId(team.id);
          // Create a new team object instead of mutating the original
          updatedTeams[i] = {
            ...team,
            playersInTeam: playersInTeam || [],
          };
        } catch (error) {
          console.error("Error getting players for team:", team.id, error);
          updatedTeams[i] = {
            ...team,
            playersInTeam: [],
          };
        }
      }

      // Update the reactive property with the new array
      this.clubTeams = updatedTeams;
    },
    async getTeamsByUserId() {
      // Null safety check
      if (!this.userData || !this.userData.id) {
        console.warn("userData or userId not available");
        return;
      }

      const data = {
        action: "getTeamsDataById",
        userId: this.userData.id,
      };

      if (this.userData.userType === "Admin" && this.userData.clubId) {
        data.clubId = this.userData.clubId;
      }

      try {
        const responce = await Teams.find(data);
        if (responce && responce.data) {
          this.clubTeams = this.removeDuplicates(responce.data, "id");
          await this.populateAllPlayersInTeam();
        }
      } catch (error) {
        console.error("Error getting teams by user ID:", error);
        this.$toast?.error("Failed to load teams");
      }
    },

    async getTeamsByCoachid() {
      // Null safety check
      if (!this.userData || !this.userData.id) {
        console.warn("userData or coach_id not available");
        return;
      }

      const data = {
        action: "getTeamsByCoachid",
        coach_id: this.userData.id,
      };

      try {
        const responce = await Teams.find(data);
        if (responce && responce.data && responce.data.length > 0) {
          this.clubTeams = this.removeDuplicates(responce.data, "id");
          await this.populateAllPlayersInTeam();
        }
      } catch (error) {
        console.error("Error getting teams by coach ID:", error);
        this.$toast?.error("Failed to load coach teams");
      }
    },

    onClubGroupsLoaded(info) {
      this.clubGroupCount = Array.isArray(info?.chatGroup)
        ? info.chatGroup.length
        : 0;
    },
    // Member pool for club groups: every active person in the club
    // (coaches, staff, players), name-sorted.
    async getClubMembers() {
      if (!this.userData?.clubId) return;
      try {
        const response = await Users.find({
          action: "getUserByClubId",
          clubId: this.userData.clubId,
        });
        const users = Array.isArray(response?.data) ? response.data : [];
        this.clubMembers = users
          .filter((u) => u && u.id && u.userType !== "Archived")
          .map((u) => ({
            id: Number(u.id),
            name: u.position && u.userType !== "User"
              ? `${u.name} (${u.position})`
              : u.name,
            userImage: u.userImage,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error("Error loading club members:", error);
      }
    },
    async getUsersTeams() {
      await this.getClubMembers();
      if (this.userData.userType === "Admin") {
        await this.getTeamsByUserId();
      } else if (this.userData.userType === "Coach") {
        await this.getTeamsByCoachid();
      } else {
        await this.getTeamsByUserId();
      }
    },
    async getUserById(clientId) {
      if (!clientId) {
        console.warn("clientId not provided");
        return false;
      }

      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };

      try {
        const response = await Users.find(userData);
        if (response && response.data && response.data.length > 0) {
          // Create a copy to prevent Vuex mutation warnings
          this.userData = { ...response.data[0] };
          const userData = { ...response.data[0] };
          this.$store.dispatch(USER_POPULATE, { userData });
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error fetching user by ID:", error);
        this.$toast?.error("Failed to load user data");
        return false;
      }
    },
    deepClone(obj) {
      // Use structuredClone if available, otherwise fall back to JSON method
      if (typeof structuredClone !== "undefined") {
        return structuredClone(obj);
      } else {
        return structuredClone(obj);
      }
    },
    setUser() {
      if (this.getProfile !== null && this.getProfile !== undefined) {
        // Create a deep copy to prevent Vuex mutation warnings
        this.userData = this.deepClone(this.getProfile);
      }
    },
    setHeaderUserData() {
      if (this.getProfile !== null && this.getProfile !== undefined) {
        // Create a deep copy to prevent Vuex mutation warnings
        this.userHeaderData = this.deepClone(this.getProfile);
      }
    },
  },
};
</script>
<style scoped lang="scss">
.multi-chats-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  .chat-group-wrapper {
    border: 1px solid #e0e5e9;
    padding: 1rem;
    min-width: 21rem;

    &.club-groups {
      border-color: #b9c8d6;
      background: #f7f9fb;
    }

    .club-groups-hint {
      font-size: 0.8rem;
      color: #5b6b7a;
      margin: -0.25rem 0 0.75rem;
    }
  }
}

.document-uploads-tab {
  padding: 2rem;
}

.signed-documents-wrapper {
  display: block;

  .signed-documents {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
}

.tabs-wrapper .tab-content {
  padding: 1rem;
}

.sorting-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: start;

  .filter-by {
    background: transparent;
  }

  .search {
    max-width: 20rem;

    input {
      padding: 0.5rem;
      font-size: 0.875rem;
      border-radius: 0;
      background: "white";
    }

    .input-group-text {
      padding: 0.5rem;
      border-radius: 0;
    }
  }
}

.access-pool {
  padding: 1rem;
  border: 1px solid #dddddd;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  .has-access {
    background: #e3f0fb;
    padding: 0.5rem;
    display: block;
    border-radius: 10px;
    cursor: pointer;

    &:hover,
    &:focus {
      background: #f8e0e0;
    }
  }
}

.buttons-row {
  margin-top: 1rem;
  padding-top: 1rem;
}

.file-upload {
  border: 1px solid #dddddd;
  padding: 1rem;
  margin-bottom: 1rem;
}

.documents-tab {
  display: flex;
  flex-wrap: wrap;

  .document {
    max-width: 14rem;
    text-align: center;
    padding: 1rem;
    margin: 1rem;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(138, 138, 138, 0.3);
    border: 1px solid transparent;

    &:hover,
    &:focus {
      border: 1px solid $secondary-color;
    }

    h3 {
      margin-top: 1rem;
      font-size: 1rem;
      overflow: hidden;
      white-space: nowrap;
    }

    img {
      width: 100%;
    }
  }
}

.doc-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;

  svg {
    border: 1px solid #c3cdd5;
    padding: 0.3rem;
    font-size: 1.7rem;

    &:hover,
    &:focus {
      cursor: pointer;
      background: #eaeff3;
    }
  }
}
</style>
